import clsx from 'clsx';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { TiptapEditor } from '@/components/ui/tiptap-editor';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { getPlainTextLength } from '@/utils/string-util';

import { MIN_JOB_DESCRIPTION_LENGTH } from '../validation/constants';

interface DescriptionSectionProps {
  openingId: string;
}

export function DescriptionSection({ openingId }: DescriptionSectionProps) {
  const intl = useIntl();
  const { hookForm } = useJobPostingContext();
  const {
    control,
    formState: { errors },
    watch,
  } = hookForm;
  const hasError = !!errors?.jobDetails?.[openingId]?.description;

  const description = watch(`jobDetails.${openingId}.description`);
  const descriptionLength = getPlainTextLength(description);

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <Typography variant="caption" className="!text-fx-neutral-500">
          {intl.formatMessage({
            defaultMessage: 'Description',
            description: 'Description of the job',
          })}
        </Typography>
      </div>

      <div className="-mt-2 flex flex-col gap-4">
        <Typography variant="small1" className="!text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="Note: Editing this field will create a unique job description for this posting. It will not be updated if the position or opening job description changes."
            description="Note about editing the job description"
          />
        </Typography>
        <Controller
          name={`jobDetails.${openingId}.description`}
          control={control}
          render={({ field }) => (
            <div
              className={clsx(
                'rounded-lg border',
                hasError ? 'border-fx-rose' : 'border-transparent',
              )}
            >
              <TiptapEditor
                content={field.value || ''}
                onChange={field.onChange}
                placeholder={intl.formatMessage({
                  defaultMessage: 'Enter job description...',
                  description: 'Placeholder for the job description editor',
                })}
              />
            </div>
          )}
        />
        <Typography
          variant="small1"
          className={clsx(
            '-mt-3',
            hasError ? '!text-fx-rose' : '!text-fx-neutral-600',
          )}
        >
          {descriptionLength >= MIN_JOB_DESCRIPTION_LENGTH ? (
            <FormattedMessage
              defaultMessage="{descriptionLength} characters"
              description="Job description length message"
              values={{ descriptionLength }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="{descriptionLength} / {minLength} characters (minimum)"
              description="Job description length error message"
              values={{
                descriptionLength,
                minLength: MIN_JOB_DESCRIPTION_LENGTH,
              }}
            />
          )}
        </Typography>
      </div>
    </>
  );
}
